import moment from 'moment';
import lodash from 'lodash';
import classNames from 'classnames';
import { Link, RouteComponentProps } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import config from 'config';

import { useQueryClient } from '@tanstack/react-query';

import * as mixpanel from 'services/mixPanel';

import history from 'utils/history';
import * as toast from 'utils/toast';
import { intersect } from 'utils/array';
import { handleError } from 'utils/errorHandler';
import { getLeaveIssueCountMessage } from 'utils/issueeCountMessage';
import { extractFullName, getFirstName, interpolate } from 'utils/string';
import { getEmployeeSelectOptions } from 'utils/user';
import { addMonths, getDateDifference, getFirstDayOfMonth, getFormattedDate } from 'utils/date';

import Loading from 'components/common/loading/Loading';
import { PendingActionsInfo } from 'components/common/issuerChange';
import { LeaveIssuersHistory } from 'components/common/issuerChange';
import InputWrapper from 'components/common/inputWrapper';
import DatePicker from 'components/common/datepicker/DatePicker';
import ProfileHeader from '../components/profileHeader/ProfileHeader';
import Info from 'components/common/inputWrapper/components/Info';
import { FormSelect } from 'components/common/form';
import AvatarComponent from 'components/common/avatar/AvatarComponent';
import { useFetchAttendanceSummary } from 'hooks/query/useFetchAttendanceSummary';
import {
  useFetchIssueeCountQuery,
  useFetchLeaveIssuerHistoryQuery,
  useFetchUserByIdQuery,
  useUpdateLeaveIssuerMutation,
  usersQueryOptions,
} from 'hooks/query/useUserQueries';
import { useMyRoleQuery } from 'hooks/query/useMyRole';

import {
  MIN_DEBOUNCE_TIME,
  TYPE_USER_TEXT,
  SEARCH_LEAVE_ISSUER_TEXT,
  MIN_USER_SEARCH_LENGTH,
  NO_USERS_FOUND_TEXT,
  REACT_DATEPICKER_DATE_FORMAT,
  ROLE,
} from 'constants/appConstants';
import { SEMI_ANNUAL } from 'constants/date';
import { mixPanelEvents } from 'constants/mixPanel';
import { ALL, MAKE_SEARCH_BY } from 'constants/fetchTypes';
import { DEFAULT_ISSUE_ALERT_COUNT, USER_TEAM_COUNT_TYPE } from 'constants/leave';
import type { LeaveIssuerHistoryItem } from 'interface/issuer';
import type { SelectOption } from 'interface/dropdownsearch';
import type { EmployeeLike } from 'interface/userUtils';
import type { SelectOption as FormSelectOption } from 'components/common/form/FormSelect';

const selectedFieldToState = {
  leaveIssuer: 'selectedLeaveIssuer',
  transitionDate: 'transitionDate',
};

interface LeaveIssuerOption extends SelectOption {
  empId?: string;
  email: string;
}
type ProfileHeaderUser = React.ComponentProps<typeof ProfileHeader>['user'];

interface LeaveIssuerUserInfo {
  name: string;
  currentLeaveIssuer: string;
  currentLeaveIssuerId: number;
}

interface LeaveIssuerEmployee {
  id: number;
  firstName: string;
  lastName: string;
  middleName?: string | null;
  joinDate?: string;
  leaveIssuer?: { id?: number; firstName: string; lastName: string; middleName?: string | null };
  [key: string]:
    | string
    | number
    | boolean
    | null
    | undefined
    | { id?: number; firstName?: string; lastName?: string; middleName?: string | null };
}

interface ChangeLeaveIssuerProps extends RouteComponentProps<{ id?: string }> {
  user: ProfileHeaderUser;
  match: { params: { id?: string } };
}

interface ChangeLeaveIssuerErrors {
  leaveIssuer: boolean;
  transitionDate: boolean;
}

interface ChangeEventPayload {
  target: {
    name: 'leaveIssuer' | 'transitionDate';
    value: string | number | LeaveIssuerOption;
  };
}

const ChangeLeaveIssuer: React.FC<ChangeLeaveIssuerProps> = ({ match, user: loggedInUser }) => {
  const id = match.params.id;
  const queryClient = useQueryClient();
  const { data: userRoles = [] } = useMyRoleQuery(loggedInUser?.id);

  const { data: employeeData, isLoading: loadingEmployee } = useFetchUserByIdQuery<LeaveIssuerEmployee>(
    id,
    Boolean(id)
  );
  const { data: leaveIssuersHistory = [], isLoading: lodadingHistory } = useFetchLeaveIssuerHistoryQuery<
    LeaveIssuerHistoryItem[]
  >(id, Boolean(id));
  const updateLeaveIssuerMutation = useUpdateLeaveIssuerMutation();
  const isSubmitting = updateLeaveIssuerMutation.isPending;

  const [loadingLeaveIssuers, setLoadingLeaveIssuers] = useState<boolean>(false);
  const [leaveIssuers, setLeaveIssuers] = useState<LeaveIssuerOption[]>([]);
  const [selectedLeaveIssuer, setSelectedLeaveIssuer] = useState<LeaveIssuerOption | null>(null);
  const [transitionDate, setTransitionDate] = useState<string | null>(null);
  const [errors, setErrors] = useState<ChangeLeaveIssuerErrors>({
    leaveIssuer: false,
    transitionDate: false,
  });
  const [leaveIssuerCode, setLeaveIssuerCode] = useState<string>('');

  const employee = employeeData;
  const user = useMemo<LeaveIssuerUserInfo>(
    () => ({
      name: employeeData ? extractFullName(employeeData) : '',
      currentLeaveIssuer: employeeData?.leaveIssuer ? extractFullName(employeeData.leaveIssuer) : 'None',
      currentLeaveIssuerId: employeeData?.leaveIssuer?.id || 0,
    }),
    [employeeData]
  );
  const currentTransitionDate = (leaveIssuersHistory.length && leaveIssuersHistory[0].transitionDate) || null;

  const attendanceParams = useMemo(
    () => ({
      fetchType: ALL,
      userId: id,
      fields: 'worklog,leave',
      startDate: getFirstDayOfMonth(1),
      endDate: addMonths(getFormattedDate(), SEMI_ANNUAL),
    }),
    [id]
  );
  const { data: attendanceSummary } = useFetchAttendanceSummary(attendanceParams, Boolean(id));
  const pendingLeaves = (attendanceSummary?.data?.leave as { requested?: number } | undefined)?.requested || 0;
  const pendingWorklogs = (attendanceSummary?.data?.worklog as { submitted?: number } | undefined)?.submitted || 0;

  const { data: issueeCountData, isFetching: loadingIssueCount } = useFetchIssueeCountQuery<{
    count: { leaveIssuee: number };
  }>(
    selectedLeaveIssuer ? Number(selectedLeaveIssuer.value) : undefined,
    { count: USER_TEAM_COUNT_TYPE.LEAVE_ISSUE },
    Boolean(selectedLeaveIssuer)
  );
  const issueCount = issueeCountData?.count?.leaveIssuee || 0;

  const userId = loggedInUser?.id;

  const getPageTitle = useCallback((employeeData: LeaveIssuerEmployee | null) => {
    const employeeName = employeeData ? extractFullName(employeeData) : '';
    return employeeName ? `${employeeName}'s leave issuer | Vyaguta` : 'Leave issuer | Vyaguta';
  }, []);

  useEffect(() => {
    if (employeeData) {
      window.document.title = getPageTitle(employeeData);
    }
  }, [employeeData, getPageTitle]);

  const getSelectedOptionValue = useCallback(
    (fieldName: ChangeEventPayload['target']['name'], fieldValue: string | number | LeaveIssuerOption) => {
      if (fieldName === 'leaveIssuer') {
        return (
          (typeof fieldValue !== 'object' &&
            leaveIssuers.find((leaveIssuer) => String(fieldValue) === String(leaveIssuer.value))) ||
          (typeof fieldValue === 'object' ? fieldValue : null)
        );
      }
      return fieldName === 'transitionDate' ? String(fieldValue) : null;
    },
    [leaveIssuers]
  );

  const handleChange = useCallback(
    (event: ChangeEventPayload) => {
      const selectedValue = event?.target?.value;
      const selectedFieldName = event?.target?.name;
      const mappedValue = getSelectedOptionValue(selectedFieldName, selectedValue);

      if (selectedFieldToState[selectedFieldName] === 'selectedLeaveIssuer') {
        setSelectedLeaveIssuer((mappedValue as LeaveIssuerOption) || null);
      } else {
        setTransitionDate((mappedValue as string) || null);
      }

      setErrors((prevErrors) => ({
        ...prevErrors,
        [selectedFieldName]: false,
      }));
    },
    [getSelectedOptionValue]
  );

  const getLeaveIssuerSelectOptions = useCallback((leaveIssuer: LeaveIssuerEmployee): LeaveIssuerOption => {
    const employeeOption = getEmployeeSelectOptions(leaveIssuer as EmployeeLike);

    return {
      ...employeeOption,
      email: String(leaveIssuer.email || ''),
    };
  }, []);

  useEffect(() => {
    if (!leaveIssuerCode) {
      return;
    }

    void (async () => {
      try {
        const { data } = await queryClient.fetchQuery(
          usersQueryOptions({
            q: leaveIssuerCode,
            searchBy: MAKE_SEARCH_BY.join(','),
            fields: 'id,empId,firstName,lastName,middleName,email',
          })
        );
        const selectOptions = (data as LeaveIssuerEmployee[]).map(getLeaveIssuerSelectOptions);
        setLeaveIssuers(selectOptions.filter(({ value: optionId }) => Number(optionId) !== Number(id)));
      } catch (error) {
        handleError(error);
      } finally {
        setLoadingLeaveIssuers(false);
      }
    })();
  }, [getLeaveIssuerSelectOptions, id, leaveIssuerCode, queryClient]);

  const firstName = selectedLeaveIssuer ? getFirstName(String(selectedLeaveIssuer.label)) : '';

  const leaveIssuerDebounceFunction = useMemo(
    () =>
      lodash.debounce((code: string) => {
        setLeaveIssuerCode(code);
      }, MIN_DEBOUNCE_TIME),
    []
  );

  useEffect(() => {
    return () => {
      leaveIssuerDebounceFunction.cancel();
    };
  }, [leaveIssuerDebounceFunction]);

  const handleLeaveIssuerCodeChange = useCallback(
    (code: string) => {
      if (code.trimStart().length < MIN_USER_SEARCH_LENGTH) {
        setLoadingLeaveIssuers(false);
        setLeaveIssuerCode('');
        return;
      }
      setLoadingLeaveIssuers(true);
      leaveIssuerDebounceFunction(code);
    },
    [leaveIssuerDebounceFunction]
  );

  const redirectToEmployeeEdit = useCallback(() => {
    history.goBack();
  }, []);

  const checkDateOutsideRange = useCallback(
    (date: moment.Moment) => {
      if (!user.currentLeaveIssuerId) {
        return getDateDifference(date, employee?.joinDate) >= 0;
      }
      return getDateDifference(date, currentTransitionDate) >= 0;
    },
    [currentTransitionDate, employee?.joinDate, user.currentLeaveIssuerId]
  );

  const handleSubmit = useCallback(async () => {
    const { leaveIssuer: changeLeaveIssuer } = mixPanelEvents.change;
    if (!selectedLeaveIssuer || !transitionDate || !id) {
      setErrors({
        leaveIssuer: !selectedLeaveIssuer,
        transitionDate: !transitionDate,
      });
      return;
    }
    try {
      updateLeaveIssuerMutation.mutate(
        {
          leaveIssuer: {
            id: selectedLeaveIssuer.value,
            email: selectedLeaveIssuer.email,
            transitionDate,
          },
          id,
        },
        {
          onSuccess: () => {
            mixpanel.trackEvent(changeLeaveIssuer);
            toast.success({
              title: 'Success',
              message: 'Leave Issuer has been updated successfully.',
            });
            redirectToEmployeeEdit();
          },
        }
      );
    } catch (error) {
      handleError(error);
    }
  }, [id, redirectToEmployeeEdit, selectedLeaveIssuer, transitionDate, updateLeaveIssuerMutation]);

  const pendingLeaveOrWorklog = pendingLeaves || pendingWorklogs;
  const isHrAdminOrSuperAdmin = Boolean(intersect(userRoles, [ROLE.SUPER_ADMIN, ROLE.HR_ADMIN]).length);
  const canEditLeaveIssuer = !pendingLeaveOrWorklog || isHrAdminOrSuperAdmin;

  if (loadingEmployee) {
    return (
      <div className="loading-container container">
        <Loading />
      </div>
    );
  }

  return (
    <>
      <div className="title profile-wrapper">
        <ProfileHeader employee={employee} user={loggedInUser} />
      </div>
      <div className="container d-flex fd-row flex-fix name-wrap leaveissuer-page-container">
        <div className="full-scope-card momr-20 leaveissuer-form-container">
          <div className="full-scope-card__header table-header name-wrap">
            <div className="d-flex flex-row">
              <h3>Change Leave Issuer</h3>
            </div>
          </div>
          <div className="leaveissuer-form">
            <label className="font-label font-14 label-margin">
              <span className="font-weight-bold dark--text"> Name: </span>
              <span className="span-padding-left">{user.name}</span>
            </label>

            <label className="font-label font-14 label-margin">
              <span className="font-weight-bold dark--text"> Current Leave Issuer: </span>
              <span className="span-padding-left">
                {user.currentLeaveIssuerId ? (
                  <Link to={interpolate(config.endpoints.lms.userProfile, { id: user.currentLeaveIssuerId })}>
                    {user.currentLeaveIssuer}
                  </Link>
                ) : (
                  <span>{user.currentLeaveIssuer}</span>
                )}
              </span>
            </label>

            <PendingActionsInfo pendingLeaves={pendingLeaves} pendingWorklogs={pendingWorklogs} user={user.name} />

            <FormSelect
              label="Leave Issuer"
              name="leaveIssuer"
              placeholder={loadingLeaveIssuers ? 'Loading' : SEARCH_LEAVE_ISSUER_TEXT}
              value={selectedLeaveIssuer ? (selectedLeaveIssuer.value as string | number) : null}
              isMandatory={true}
              error={errors.leaveIssuer ? 'Please Select a Leave Issuer' : null}
              handleChange={(event) =>
                handleChange({
                  target: {
                    name: event.target.name as 'leaveIssuer' | 'transitionDate',
                    value: event.target.value as string | number | LeaveIssuerOption,
                  },
                })
              }
              options={leaveIssuers as FormSelectOption[]}
              isLoading={loadingLeaveIssuers}
              noOptionsMessage={() => (leaveIssuerCode.length ? NO_USERS_FOUND_TEXT : TYPE_USER_TEXT)}
              handleInputChange={handleLeaveIssuerCodeChange}
              iconComponent={(option) => <AvatarComponent label={option.label} value={option as LeaveIssuerOption} />}
              isClearable={true}
              disabled={!canEditLeaveIssuer}
            />

            {selectedLeaveIssuer && !loadingIssueCount && (
              <Info
                message={getLeaveIssueCountMessage({
                  issuerName: firstName,
                  issueCount: issueCount,
                  isLoggedInUser: selectedLeaveIssuer?.value === userId,
                })}
                shouldAlert={issueCount >= DEFAULT_ISSUE_ALERT_COUNT}
              />
            )}

            <InputWrapper
              isMandatory={true}
              label="Transition Date"
              error={errors.transitionDate ? 'Please Select a transition date' : null}
            >
              <DatePicker
                date={transitionDate ? moment(transitionDate) : null}
                placeholderText="Pick a Transition Date"
                dateFormat={REACT_DATEPICKER_DATE_FORMAT}
                displayFormat="LL"
                hasError={Boolean(errors.transitionDate)}
                isOutsideRange={checkDateOutsideRange}
                showMonthDropdown={true}
                showYearDropdown={true}
                openDirection="up"
                onDateChange={(selectedDate) =>
                  handleChange({
                    target: {
                      name: 'transitionDate',
                      value: moment(selectedDate).format('YYYY-MM-DD'),
                    },
                  })
                }
              />
            </InputWrapper>
            <button
              type="button"
              className="btn btn--outlined-grey f-left card-button mr-10 mb-20"
              onClick={redirectToEmployeeEdit}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className={classNames('btn f-right card-button', {
                'btn--primary': !isSubmitting && canEditLeaveIssuer,
                'btn--disabled': isSubmitting || !canEditLeaveIssuer,
                'cursor-not-allowed': !canEditLeaveIssuer,
              })}
              onClick={handleSubmit}
              disabled={isSubmitting || !canEditLeaveIssuer}
            >
              {isSubmitting ? <Loading /> : 'Update'}
            </button>
          </div>
        </div>
        <LeaveIssuersHistory leaveIssuers={leaveIssuersHistory} isLoading={lodadingHistory} />
      </div>
    </>
  );
};

export default ChangeLeaveIssuer;
